import { api } from './api';

export interface SubcategoryDTO {
  id: string;
  name: string;
  categoryId: string;
  displayOrder: number;
  body?: string;
  visibility?: 'private' | 'org' | 'team' | 'public';
  teamId?: string | null;
  discoverable?: boolean;
  /** Set when the row was returned as a locked stub. */
  locked?: true;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
  coverImage?: string;
}

export interface CreateSubcategoryInput {
  name: string;
  categoryId: string;
  branchId: string;
  visibility?: 'private' | 'org' | 'team' | 'public';
  teamId?: string | null;
  discoverable?: boolean;
}

export interface RenameSubcategoryInput {
  name: string;
  branchId: string;
}

export interface ReorderItem {
  type: 'subcategory' | 'content';
  id: string;
}

export interface ReorderInput {
  categoryId: string;
  branchId: string;
  order: ReorderItem[];
}

export interface UpdateSubcategoryInput {
  branchId: string;
  name?: string;
  body?: string;
  coverImage?: string;
}

export interface MoveContentInput {
  branchId: string;
  subcategoryId: string | null;
  displayOrder: number;
}

export const subcategoryApi = {
  list(categoryId: string, branchId?: string): Promise<SubcategoryDTO[]> {
    const params = new URLSearchParams({ categoryId });
    if (branchId) params.set('branchId', branchId);
    return api.get<SubcategoryDTO[]>(`/subcategories?${params.toString()}`);
  },

  create(input: CreateSubcategoryInput): Promise<SubcategoryDTO> {
    return api.post<SubcategoryDTO>('/subcategories', input);
  },

  rename(id: string, input: RenameSubcategoryInput): Promise<SubcategoryDTO> {
    return api.patch<SubcategoryDTO>(`/subcategories/${id}`, input);
  },

  update(id: string, input: UpdateSubcategoryInput): Promise<SubcategoryDTO> {
    return api.patch<SubcategoryDTO>(`/subcategories/${id}`, input);
  },

  delete(id: string, branchId: string): Promise<{ deletedSubcategory: string; deletedContentCount: number }> {
    return api.delete<{ deletedSubcategory: string; deletedContentCount: number }>(
      `/subcategories/${id}?branchId=${encodeURIComponent(branchId)}`
    );
  },

  reorder(input: ReorderInput): Promise<{ updated: number }> {
    return api.put<{ updated: number }>('/subcategories/reorder', input);
  },

  reorderContents(subcategoryId: string, input: { branchId: string; order: string[] }): Promise<{ updated: number }> {
    return api.put<{ updated: number }>(`/subcategories/${subcategoryId}/reorder-contents`, input);
  },

  moveContent(contentId: string, input: MoveContentInput): Promise<unknown> {
    return api.patch(`/contents/${contentId}/move`, input);
  },
};
